import { Box, Flex, Loader, Stack } from '@mantine/core';
import {
  DiagnosticReportDisplay,
  Timeline,
  TimelineItem,
  useMedplum,
} from '@medplum/react';
import { FC } from 'react';
import { usePatient } from '@hooks/usePatient';
import { WithId } from '@medplum/core';
import { Bundle, DiagnosticReport, RiskAssessment } from '@medplum/fhirtypes';
import { RiskAssessmentCard } from './RiskAssessmentCard';

// -----------------------------------------------------------------------------
interface DiagnosticReportTimelineProps {
  patientId?: string;
  riskAssessmentReference?: string;
  diagnosticReportLimit?: number;
  flexDirection?: 'row' | 'column-reverse';
}

// -----------------------------------------------------------------------------
export const DiagnosticReportTimeline: FC<DiagnosticReportTimelineProps> = ({
  patientId,
  riskAssessmentReference,
  diagnosticReportLimit = 100,
  flexDirection = 'row',
}) => {
  const patient = usePatient({
    patientId: patientId,
  });

  const medplum = useMedplum();

  // ---------------------------------------------------------------------------
  if (!patient) {
    return <Loader />;
  }

  // ---------------------------------------------------------------------------
  const diag: Bundle<WithId<DiagnosticReport>> = medplum
    .search('DiagnosticReport', {
      patient: patient.id,
      _sort: '-date',
      _count: diagnosticReportLimit,
    })
    .read();

  // ---------------------------------------------------------------------------
  const risk: Bundle<WithId<RiskAssessment>> = medplum
    .search('RiskAssessment', {
      patient: patient.id,
      _sort: '-date',
      ...(riskAssessmentReference
        ? { _id: riskAssessmentReference }
        : {
            _count: 100,
          }),
    })
    .read();

  // ---------------------------------------------------------------------------
  return (
    <Flex direction={flexDirection} align="start" w="100%" gap="md">
      <Box {...(flexDirection === 'row' ? { maw: '65%' } : {})}>
        <Timeline>
          {diag.entry?.map((entry) => {
            const report = entry.resource as DiagnosticReport;
            return (
              <TimelineItem resource={report} padding={true}>
                <DiagnosticReportDisplay value={report} />
              </TimelineItem>
            );
          })}
        </Timeline>
      </Box>

      <Stack {...(flexDirection === 'row' ? { maw: '30%' } : {})}>
        {risk.entry?.map((entry) => {
          const assessment = entry.resource as RiskAssessment;
          return <RiskAssessmentCard riskAssessment={assessment} />;
        })}
      </Stack>
    </Flex>
  );
};
